// src/pages/Admin/AdminBlogForm.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { blogAPI } from '../../api/blog.js';
import { useToast } from '../../hooks/useToast.js';
import { Button } from '../../components/ui/Button/Button.jsx';
import { Input } from '../../components/ui/Input/Input.jsx';

export const AdminBlogForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error } = useToast();
  const isEdit = id && id !== 'new';
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    category: 'other',
    tags: [],
    status: 'draft',
    published_at: '',
  });

  useEffect(() => {
    if (isEdit) {
      loadPost();
    }
  }, [id]);

  const loadPost = async () => {
    setIsLoading(true);
    try {
      const response = await blogAPI.getById(id);
      const post = response.data.data;
      setFormData({
        ...post,
        tags: post.tags?.join(', ') || '',
        published_at: post.published_at ? post.published_at.split('T')[0] : '',
      });
    } catch (err) {
      error('Не удалось загрузить статью');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug || !formData.content) {
      error('Заполните обязательные поля');
      return;
    }

    const submitData = {
      ...formData,
      tags: formData.tags.split(',').map(tag => tag.trim()).filter(Boolean),
    };

    setIsLoading(true);
    try {
      if (isEdit) {
        await blogAPI.update(id, submitData);
        success('Статья обновлена');
      } else {
        await blogAPI.create(submitData);
        success('Статья создана');
      }
      navigate('/admin/blog');
    } catch (err) {
      error('Не удалось сохранить статью');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading && isEdit) {
    return <div style={{ textAlign: 'center', padding: '40px' }}>Загрузка...</div>;
  }

  return (
    <div style={{
      backgroundColor: 'rgb(var(--c-surface))',
      borderRadius: '12px',
      padding: '24px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      <form onSubmit={handleSubmit}>
        <h2 style={{ fontSize: '20px', fontWeight: 600, marginBottom: '24px' }}>
          {isEdit ? 'Редактировать статью' : 'Новая статья'}
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <Input
            label="Заголовок *"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
          <Input
            label="Slug *"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            required
          />
        </div>

        <div style={{ height: '12px' }} />

        <Input
          label="Краткое описание"
          value={formData.excerpt}
          onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
        />
        <div style={{ height: '12px' }} />

        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
            Содержание *
          </label>
          <textarea
            value={formData.content}
            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
            style={{
              width: '100%',
              padding: '10px 16px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              fontSize: '14px',
              outline: 'none',
              resize: 'vertical',
              minHeight: '300px',
              fontFamily: 'monospace',
            }}
            required
          />
        </div>

        <div style={{ height: '12px' }} />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
          <Input
            label="URL обложки"
            value={formData.cover_image}
            onChange={(e) => setFormData({ ...formData, cover_image: e.target.value })}
          />
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Категория
            </label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="guides">Гайды</option>
              <option value="cases">Кейсы</option>
              <option value="news">Новости</option>
              <option value="tutorials">Обучение</option>
              <option value="other">Другое</option>
            </select>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, marginBottom: '4px' }}>
              Статус
            </label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              style={{
                width: '100%',
                padding: '8px 12px',
                borderRadius: '8px',
                border: '1px solid rgb(var(--c-border))',
                fontSize: '14px',
                outline: 'none',
              }}
            >
              <option value="draft">Черновик</option>
              <option value="published">Опубликовать</option>
              <option value="archived">Архив</option>
            </select>
          </div>
        </div>

        <div style={{ height: '12px' }} />

        <Input
          label="Теги (через запятую)"
          value={formData.tags}
          onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
          placeholder="реклама, маркетинг, дизайн"
        />

        <div style={{ height: '24px' }} />

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button type="submit" isLoading={isLoading}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button type="button" variant="secondary" onClick={() => navigate('/admin/blog')}>
            Отмена
          </Button>
        </div>
      </form>
    </div>
  );
};